import { useAtomValue } from 'jotai';
import { useCallback, useState } from 'react';
import { Button, styled, Typography } from '@mui/material';
import { set } from 'lodash-es';
import { kpiConfigAtom } from '../../../services/state/KPIConfigState';
import { kpiGroupsByIdState, kpiGroupsState } from '../../../services/state/KPI/KPITemplatesStateJ';
import { IField } from '../../../data-models/field2.data-model';
import { BasicDialog } from '../../../components/Dialog/BasicDialog';
import { BasicDragHandle, SortableItem } from '../../../components/SortableList/SortableItem';
import { MultiSortableList } from '../../../components/SortableMultiList/MultiSortableList';
import { Card } from '../../../components/Card/Card';
import { StickyFormButtons } from '../../../components/Form/StickyFormButtons';
import { KPIGroup } from '../../../schemas/kpi-group.schema';
import { useConfigActions } from '../Common/UseConfigActions';
import { sortGenericSortable } from '../../../data-models/app-config.data-model';
import { KPIConfigBaseURL, sortKpisByType } from './ConfigKPI';
import { getKPIsByGroupSorted, KPINoGroupName } from './KPIFields';

const CardWrapper = styled('div')`
  display: grid;
  grid-template-columns: 1fr min-content;
  align-items: center;
  width: 100%;
`;

const ContentWrapper = styled('div')`
  padding: 2rem;
`;

export interface IKPIGroupSortProps {
  closeModal: () => void;
}

/** Adds every group, plus No Group, even with no KPIs yet, so KPIs can be dragged into it. */
export function withEmptyKPIGroups(kpisByGroup: Record<string, IField<unknown>[]>, kpiGroups: KPIGroup[]) {
  const result = { ...kpisByGroup };
  [...kpiGroups.map((group) => group.name), KPINoGroupName].forEach((groupName) => {
    if (!result[groupName]) {
      result[groupName] = [];
    }
  });
  return result;
}

// The API takes null to clear a KPI's group; the shared IField type only allows number | undefined.
export type KPISortUpdate = Omit<IField<unknown>, 'kpiGroupId'> & { kpiGroupId: number | null };

export function getKPISortPayload(
  updatedItems: Record<string, IField<unknown>[]>,
  kpiGroups: KPIGroup[]
): KPISortUpdate[] {
  const kpiGroupNameToId = kpiGroups.reduce(
    (res, group) => {
      res[group.name] = group.id;
      return res;
    },
    {} as Record<string, number>
  );
  const payload: KPISortUpdate[] = [];
  let index = 0;

  Object.keys(updatedItems).forEach((groupName) => {
    updatedItems[groupName].forEach((kpi) => {
      const updatedKpi: KPISortUpdate = {
        ...kpi,
        // null, not undefined: JSON drops undefined, which would leave the old group in place.
        kpiGroupId: groupName === KPINoGroupName ? null : kpiGroupNameToId[groupName],
        sortOrder: index,
      };
      set(updatedKpi, 'gridMeta.group', groupName === KPINoGroupName ? '' : groupName);
      payload.push(updatedKpi);
      index++;
    });
  });

  return payload.sort(sortGenericSortable);
}

export function KPISort(props: IKPIGroupSortProps) {
  const { bulkUpdateItems } = useConfigActions<KPISortUpdate>(KPIConfigBaseURL);
  const universalKPIsFields = [...(useAtomValue(kpiConfigAtom) ?? [])].sort(sortKpisByType);
  const kpiGroups = useAtomValue(kpiGroupsState);
  const kpiGroupsById = useAtomValue(kpiGroupsByIdState);
  const kpisByGroup = withEmptyKPIGroups(getKPIsByGroupSorted(universalKPIsFields, kpiGroupsById), kpiGroups);
  const [updatedItems, setUpdatedItems] = useState(kpisByGroup);

  function onOrderChange(items: Record<string, IField<unknown>[]>) {
    setUpdatedItems(items);
  }

  function onSaveUpdatedOrder() {
    bulkUpdateItems(getKPISortPayload(updatedItems, kpiGroups));
  }

  return (
    <ContentWrapper>
      <MultiSortableList
        items={updatedItems}
        onChange={onOrderChange}
        renderItem={(item) => {
          return (
            <SortableItem id={item.id}>
              <Card>
                <CardWrapper>
                  <Typography>{item.displayName}</Typography>
                  <BasicDragHandle />
                </CardWrapper>
              </Card>
            </SortableItem>
          );
        }}
      />
      <StickyFormButtons onCancel={props.closeModal} onSubmit={onSaveUpdatedOrder} submitLabel={'Save'} />
    </ContentWrapper>
  );
}

export function SortKPIs() {
  const [open, setOpen] = useState(false);
  const openModal = useCallback(() => {
    setOpen(true);
  }, []);
  const closeModal = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <>
      <Button color={'secondary'} onClick={openModal} variant={'contained'}>
        Sort KPIs
      </Button>
      {open && (
        <BasicDialog open={open} onClose={closeModal} title={'Sort KPIs'}>
          <KPISort closeModal={closeModal} />
        </BasicDialog>
      )}
    </>
  );
}
